import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader2, Users, UserCheck, Award, TrendingDown } from "lucide-react";
import { fetchCourseAnalytics } from "../../redux/slices/courseSlice";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";

function toCount(v) {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function toPercent(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0%";
  return `${Math.round(n * 10) / 10}%`;
}

function CourseAnalyticsPanel({ courseId }) {
  const dispatch = useDispatch();
  const { analytics, loading } = useSelector((s) => s.course);

  useEffect(() => {
    if (courseId) dispatch(fetchCourseAnalytics(courseId));
  }, [dispatch, courseId]);

  const stats = [
    {
      title: "Total enrollments",
      value: String(toCount(analytics?.total_enrollments)),
      icon: Users,
    },
    {
      title: "Active",
      value: String(toCount(analytics?.active_enrollments)),
      icon: UserCheck,
    },
    {
      title: "Completed",
      value: String(toCount(analytics?.completed_enrollments)),
      icon: Award,
    },
    {
      title: "Dropped",
      value: String(toCount(analytics?.dropped_enrollments)),
      icon: TrendingDown,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Analytics</CardTitle>
        <CardDescription>
          Enrollment and performance summary for this course
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading && !analytics ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !analytics ? (
          <p className="text-sm text-muted-foreground py-4">
            No analytics available.
          </p>
        ) : (
          <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {stats.map((stat) => {
                const Icon = stat.icon;
                return (
                  <div
                    key={stat.title}
                    className="rounded-lg border bg-card p-4 shadow-sm"
                  >
                    <div className="flex items-center justify-between pb-2">
                      <p className="text-sm font-medium text-muted-foreground">
                        {stat.title}
                      </p>
                      <Icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <p className="text-2xl font-bold">{stat.value}</p>
                  </div>
                );
              })}
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">Completion rate</p>
                <p className="text-xl font-semibold">
                  {toPercent(analytics.completion_rate)}
                </p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">Average score</p>
                <p className="text-xl font-semibold">
                  {analytics.average_score ?? 0}
                </p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">Average streak</p>
                <p className="text-xl font-semibold">
                  {analytics.average_streak ?? 0} days
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CourseAnalyticsPanel;
